import { gql } from '@apollo/client';
import { useState } from 'react';
import { z } from 'zod';

import { EMBEDDED, MessageMedium } from 'constants/messageMediums';
import { DisplayPriorityProvider } from 'modules/placements/context/DisplayPriorityProvider';
import { DisplayPriorityTakeover } from 'modules/placements/takeovers';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import BaseSendEditor, { BaseSendEditorProps } from '../BaseSendEditor';

const EmbeddedExclusiveSchema = z.object({
  placementId: z.coerce.number({
    /*
     * Value is set as a string because of constraints with
     * BasicFilterSelect component used for placements when
     * there are more than 5 options. This value is then
     * coerced to NaN if no placement selection is made so
     * it will catch on invalid type error instead of required
     */
    invalid_type_error: 'Placement required to continue',
  }),
});

export const FetchPlacementIdsQuery = gql`
  query FetchPlacements($nonArchivedOnly: Boolean) {
    placements(nonArchivedOnly: $nonArchivedOnly) {
      payload {
        id
      }
    }
  }
`;

export const FetchEmbeddedResourcesQuery = gql`
  query fetchEmbeddedResources($campaignId: Long!) {
    campaign(id: $campaignId) {
      embeddedPriorityOrder
    }
  }
`;

export const SendEmbeddedEditor = (props: BaseSendEditorProps) => {
  const campaign = props.resources?.campaign || {};
  const { campaignId } = props.nodeData || {};
  const { embeddedPriorityOrder, campaignTypeDefinition, placementId } = campaign;

  const [isDisplayPriorityOpen, setIsDisplayPriorityOpen] = useState(false);

  const { data, isLoading } = useGraphql(FetchPlacementIdsQuery, {
    variables: {
      nonArchivedOnly: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { fetch: fetchResources, data: fetchedData } = useLazyGraphql(FetchEmbeddedResourcesQuery, {
    variables: {
      campaignId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const campaignData = {
    id: campaignId,
    placementId,
    embeddedPriorityOrder,
    campaignTypeDefinition,
    ...(fetchedData?.campaign || {}),
  };

  return (
    <DisplayPriorityProvider campaign={campaignData}>
      <BaseSendEditor
        shouldShowDeliverySectionHeader
        schema={EmbeddedExclusiveSchema}
        onDisplayPriorityOpen={() => setIsDisplayPriorityOpen(true)}
        embeddedPriorityOrder={fetchedData?.campaign?.embeddedPriorityOrder}
        hasPlacements={data?.placements?.length > 0}
        isLoading={isLoading}
        {...props}
        messageMedium={EMBEDDED as unknown as MessageMedium}
      />
      {isDisplayPriorityOpen && (
        <DisplayPriorityTakeover
          currentCampaignId={campaignId}
          isOverlay={false}
          onClose={() => {
            setIsDisplayPriorityOpen(false);
            // Re-fetch display priority
            fetchResources();
          }}
        />
      )}
    </DisplayPriorityProvider>
  );
};
